import React from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Search, Send, ArrowDown, Clock, Check, CheckCheck, AlertCircle, AlertTriangle, RotateCw, Paperclip, Smile, X as CloseIcon, FileText, Image as ImageIcon, Mic, Video as VideoIcon, Users, User as UserIcon, Shield } from 'lucide-react';
import Linkify from 'linkify-react';
import { messagerieApi, channelsApi } from '../lib/messagerieApi';
import { api, API } from '../lib/api';
import { frElapsedShort } from '../lib/formatters';
import { ROLES } from '../lib/routeConfig';
import { toastError } from '../lib/toast';

// Phase 18 → 19 → 3.1 — Messagerie interne.
// - Phase 18 : squelette WhatsApp mono-canal.
// - Phase 19 : bridge WhatsApp câblé (envoi/récep RÉELS).
// - Phase 3.1 (UX polish) :
//     • mark-read explicite (unread_count effacé quand fil visible + chargé)
//     • séparateurs de date (Africa/Casablanca — "Aujourd'hui" / "Hier" /
//       jour de la semaine (<7j) / date longue française)
//     • statuts d'envoi (horloge=sending / check=sent / alerte rouge=failed)
//       + retry cliquable sur "failed" (réutilise le même document en base)
//     • rendu WhatsApp markdown (bold *_*, italic *_*, strike *~*) + URLs
//       auto-linkées (linkify-react, JAMAIS dangerouslySetInnerHTML → 100%
//       React nodes → XSS-safe par défaut).

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
const CASA_TZ = 'Africa/Casablanca';

function initialsFrom(name, phone) {
    const src = (name || '').trim() || (phone || '').slice(-4);
    return (src.match(/[\p{L}0-9]/gu) || []).slice(0, 2).join('').toUpperCase() || '?';
}

// Phase 4 — palette DÉTERMINISTE pour les avatars fallback (initiales).
// Même wa_id → même couleur à chaque session (hash simple sur la chaîne).
// Palette pastel (10 couleurs) pour rester lisible sur fond blanc / bulles.
const AVATAR_PALETTE = [
    '#4B3FD8', // indigo (primary)
    '#9B7FF4', // violet clair
    '#059669', // vert
    '#0891B2', // cyan
    '#D97706', // orange
    '#DC2626', // rouge
    '#DB2777', // fuchsia
    '#7C3AED', // pourpre
    '#0284C7', // bleu
    '#4E7A2E', // olive (accent WhatsApp)
];

function hashStringDjb2(s) {
    // djb2 stable & rapide. On garde 31 bits pour éviter les négatifs.
    let h = 5381;
    if (!s) return 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

function avatarColorFor(key) {
    if (!key) return AVATAR_PALETTE[0];
    return AVATAR_PALETTE[hashStringDjb2(String(key)) % AVATAR_PALETTE.length];
}

// Avatar réutilisable : rendu photo si `r2Key` est fourni (via presigned URL),
// sinon initiales colorées déterministes. `sizeClass` pour la taille tailwind.
function Avatar({ name, phone, waId, r2Key, sizeClass = 'w-9 h-9', textClass = 'text-xs', testId }) {
    const src = r2Key ? messagerieApi.mediaHref(r2Key) : null;
    const key = waId || phone || name || '';
    const bg = avatarColorFor(key);
    const initials = initialsFrom(name, phone);
    if (src) {
        return (
            <img
                src={src}
                alt={name || phone || 'avatar'}
                data-testid={testId}
                data-avatar-color={bg}
                data-avatar-key={key}
                className={`${sizeClass} rounded-full object-cover shrink-0 border border-[#E5E7EB]`}
            />
        );
    }
    return (
        <div
            data-testid={testId}
            data-avatar-color={bg}
            data-avatar-key={key}
            className={`${sizeClass} rounded-full shrink-0 flex items-center justify-center font-semibold text-white ${textClass}`}
            style={{ backgroundColor: bg }}
        >
            {initials}
        </div>
    );
}

function relativeFromISO(iso) {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    return frElapsedShort(s);
}

// Retourne la clé "YYYY-MM-DD" du jour Casablanca pour un timestamp ISO.
// Toutes les frontières de jour sont évaluées en `Africa/Casablanca`.
function casaDayKey(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    // 'en-CA' garantit le format YYYY-MM-DD.
    return d.toLocaleDateString('en-CA', { timeZone: CASA_TZ });
}

// Nombre de jours calendaires (Casablanca) entre `msgIso` et maintenant.
function casaDaysAgo(msgIso, nowIso) {
    const a = casaDayKey(nowIso);
    const b = casaDayKey(msgIso);
    if (!a || !b) return NaN;
    const [ya, ma, da] = a.split('-').map(Number);
    const [yb, mb, db] = b.split('-').map(Number);
    // Dates naïves — le calcul de différence en jours n'a pas besoin de tz
    // car les composants Y/M/D sont déjà dans le fuseau Casa.
    const A = Date.UTC(ya, ma - 1, da);
    const B = Date.UTC(yb, mb - 1, db);
    return Math.round((A - B) / 86400000);
}

// Capitalise la première lettre (fr-FR renvoie "lundi" en minuscule).
function capFirst(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatSeparatorLabel(iso, nowIso) {
    const diff = casaDaysAgo(iso, nowIso);
    if (isNaN(diff)) return '';
    if (diff <= 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    // Phase A.1 — tout jour antérieur : "lundi 20 octobre" (locale fr, minuscules,
    // fuseau Casablanca). Pas d'année : correspond au format WhatsApp natif.
    return new Date(iso).toLocaleDateString('fr-FR', {
        timeZone: CASA_TZ, weekday: 'long', day: 'numeric', month: 'long',
    }).toLowerCase();
}

// Heure "HH:MM" en fuseau Casablanca.
function casaTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('fr-FR', {
        timeZone: CASA_TZ, hour: '2-digit', minute: '2-digit',
    });
}

// Phase 2 — mapping MIME → kind Whapi. Utilisé par le composer avant upload.
function kindFromFile(file) {
    const t = ((file && file.type) || '').toLowerCase();
    if (t.startsWith('image/')) return 'image';
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('audio/')) return 'voice';
    return 'document';
}

// WhatsApp markdown minimaliste : *bold*, _italic_, ~strike~.
// - Marqueurs jamais imbriqués (WhatsApp non plus).
// - Le contenu interne ne peut pas commencer/finir par un espace.
// - Marqueurs non fermés → texte brut (le regex ne matche pas, donc rien).
const MD_RE = /(\*|_|~)(?=\S)([^*_~\n]+?)(?<=\S)\1/g;

function parseWhatsAppMarkdown(text) {
    if (!text) return [];
    const parts = [];
    let cursor = 0;
    let m;
    MD_RE.lastIndex = 0;
    while ((m = MD_RE.exec(text)) !== null) {
        if (m.index > cursor) {
            parts.push({ type: 'text', text: text.slice(cursor, m.index) });
        }
        const marker = m[1];
        const inner = m[2];
        const type = marker === '*' ? 'bold' : marker === '_' ? 'italic' : 'strike';
        parts.push({ type, text: inner });
        cursor = m.index + m[0].length;
    }
    if (cursor < text.length) {
        parts.push({ type: 'text', text: text.slice(cursor) });
    }
    return parts.length ? parts : [{ type: 'text', text }];
}

// Options `linkify-react` : ouvre les liens dans un nouvel onglet et
// n'accepte que les URLs (email détecté aussi mais rendu comme lien mailto,
// on le laisse par défaut). L'attribut `rel` bloque les fuites de referrer
// et empêche `window.opener`.
const LINKIFY_OPTS = {
    target: '_blank',
    rel: 'noopener noreferrer',
    className: 'underline underline-offset-2 hover:opacity-80',
    defaultProtocol: 'https',
};

// -----------------------------------------------------------------------
// Root page
// -----------------------------------------------------------------------
export default function MessageriePage() {
    const [user, setUser] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [conversations, setConversations] = React.useState(null);
    const [loadingConvs, setLoadingConvs] = React.useState(true);
    const [selectedId, setSelectedId] = React.useState(null);
    const [thread, setThread] = React.useState(null);
    const [loadingThread, setLoadingThread] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    // Phase 2 — file d'attente de médias sélectionnés (avant envoi).
    const [attachedFiles, setAttachedFiles] = React.useState([]);
    const [uploadState, setUploadState] = React.useState(null); // {index, total, percent} | null
    // Phase 19 — bridge status ("ready" | "waiting" | "unreachable" | null=loading)
    const [bridgeStatus, setBridgeStatus] = React.useState(null);
    // Phase 3 — canal admin (`null` = "Tous les canaux") + liste channels.
    const [channelFilter, setChannelFilter] = React.useState(null);
    const [channels, setChannels] = React.useState(null); // liste des 3 canaux (admin)
    // Phase 4 — filtre type conversation: 'all' (défaut) | 'individual' | 'group'
    const [typeFilter, setTypeFilter] = React.useState('all');
    // Phase 4 — panneau participants (groupe uniquement).
    const [participantsOpen, setParticipantsOpen] = React.useState(false);
    const [groupInfo, setGroupInfo] = React.useState(null);
    const [loadingGroup, setLoadingGroup] = React.useState(false);
    // Phase 5 — killswitch bridge : bannière lecture-seule quand désactivé.
    const [bridgeActive, setBridgeActive] = React.useState(true);
    React.useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/messagerie/bridge-flag`, { credentials: 'include' });
                if (res.ok) {
                    const j = await res.json();
                    setBridgeActive(!!j.bridge_active);
                }
            } catch { /* silencieux */ }
        })();
    }, []);

    // Boot: whoami + first list load
    React.useEffect(() => {
        (async () => {
            try {
                const me = await api.me();
                setUser(me);
            } catch (e) { /* app-level auth guard already redirects */ }
        })();
    }, []);

    // Poll bridge status every 60s.
    React.useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await messagerieApi.bridgeStatus();
                if (!cancelled) setBridgeStatus(res?.status || 'unreachable');
            } catch (e) {
                if (!cancelled) setBridgeStatus('unreachable');
            }
        };
        tick();
        const iv = setInterval(tick, 60000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    const canFilterByOwner = user && (user.role === ROLES.SUPER_ADMIN || user.role === ROLES.MANAGER);
    const isAdmin = canFilterByOwner;

    // Phase 3 — Admin: charge la liste des 3 canaux + poll status toutes les 30s.
    // Agent: pas d'appel (403), on affiche uniquement son badge de canal
    // via `user.assigned_channel_id`.
    React.useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await channelsApi.list();
                if (!cancelled) setChannels(res.items);
            } catch (e) { /* silencieux — non-bloquant pour l'UX */ }
        };
        tick();
        const iv = setInterval(tick, 30000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [isAdmin]);

    // (Re)load conversations on search or channelFilter change.
    React.useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoadingConvs(true);
            try {
                const params = {};
                if (search.trim()) params.search = search.trim();
                if (isAdmin && channelFilter) params.channel_id = channelFilter;
                if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
                const res = await messagerieApi.listConversations(params);
                if (!cancelled) setConversations(res.items);
            } catch (e) {
                if (!cancelled) toastError(e?.detail || e?.message || 'Erreur de chargement');
            } finally {
                if (!cancelled) setLoadingConvs(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search, isAdmin, channelFilter, typeFilter]);

    // Phase 3.1 — sélection : efface localement le badge immédiat, puis
    // charge le fil. Le mark-read serveur intervient APRÈS chargement OK
    // (voir effet suivant) pour ne pas consommer l'unread si le GET échoue.
    const handleSelect = React.useCallback((id) => {
        setSelectedId(id);
        setConversations((prev) => prev
            ? prev.map((c) => c.id === id && (c.unread_count || 0) > 0
                ? { ...c, unread_count: 0 }
                : c)
            : prev);
    }, []);

    // Load thread when a conversation is selected + mark-read côté serveur
    // une fois messages chargés (Phase 3.1).
    React.useEffect(() => {
        if (!selectedId) { setThread(null); return; }
        let cancelled = false;
        (async () => {
            setLoadingThread(true);
            try {
                const res = await messagerieApi.getMessages(selectedId, { page_size: 200 });
                if (cancelled) return;
                setThread(res);
                // Fire-and-forget mark-read : idempotent, silencieux en cas
                // d'erreur (badge déjà effacé localement, cohérence remontera
                // au prochain fetch de conversations).
                messagerieApi.markRead(selectedId, { silent: true }).catch(() => {});
                // Phase A.1 — résolution à la demande du contact_name via
                // Whapi (uniquement si conv Whapi individuelle sans nom).
                // Fire-and-forget, silencieux : en preview sans token Whapi
                // le backend renvoie `refreshed:false` sans erreur.
                const conv = res?.conversation;
                if (conv && conv.channel_id && conv.type !== 'group' && !conv.contact_name) {
                    messagerieApi.refreshContactName(selectedId).then((r) => {
                        if (cancelled || !r || !r.refreshed) return;
                        const newName = r.conversation?.contact_name;
                        if (!newName) return;
                        setThread((prev) => prev && prev.conversation?.id === selectedId
                            ? { ...prev, conversation: { ...prev.conversation, contact_name: newName } }
                            : prev);
                        setConversations((prev) => prev ? prev.map((c) =>
                            c.id === selectedId ? { ...c, contact_name: newName } : c
                        ) : prev);
                    }).catch(() => {});
                }
            } catch (e) {
                if (!cancelled) toastError(e?.detail || e?.message || 'Erreur de chargement du fil');
            } finally {
                if (!cancelled) setLoadingThread(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedId]);

    // Phase 4 — Fermer le panneau participants + reset groupInfo quand on
    // change de conversation. Si la nouvelle conv est un groupe, on
    // pré-charge dès l'ouverture du fil (léger, cache local).
    React.useEffect(() => {
        setParticipantsOpen(false);
        setGroupInfo(null);
    }, [selectedId]);

    const openParticipants = React.useCallback(async () => {
        const conv = thread?.conversation;
        if (!conv || conv.type !== 'group') return;
        const gid = conv.group_id || conv.wa_id;
        if (!gid) return;
        setParticipantsOpen(true);
        if (groupInfo && groupInfo.group_id === gid) return; // cache
        setLoadingGroup(true);
        try {
            const res = await messagerieApi.getGroup(gid);
            setGroupInfo(res.group || null);
        } catch (e) {
            toastError(e?.detail || e?.message || 'Impossible de charger le groupe');
        } finally {
            setLoadingGroup(false);
        }
    }, [thread, groupInfo]);

    const submitMessage = async () => {
        const text = draft.trim();
        // Whapi routing hint: if the current conv came from Whapi, forward
        // the channel_id in the send payload. Legacy conversations don't
        // carry `channel_id` and the backend falls back to the bridge.
        const channelId = thread?.conversation?.channel_id;
        // If media queued → dispatch to the media batch handler.
        if (attachedFiles.length > 0) {
            return submitMediaBatch({ text, channelId });
        }
        if (!text || !selectedId) return;
        setSending(true);
        try {
            const payload = { conversation_id: selectedId, body: text };
            if (channelId) {
                payload.channel_id = channelId;
                payload.message_type = 'text';
                payload.text_content = text;
            }
            const res = await messagerieApi.send(payload, { silent: true });
            setDraft('');
            setThread((prev) => prev ? { ...prev, items: [...prev.items, res.message] } : prev);
            setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                ...c,
                last_message_preview: text.slice(0, 100),
                last_message_timestamp: res.message.timestamp,
            } : c) : prev);
        } catch (e) {
            let msg = 'Envoi impossible';
            if (e?.detail && typeof e.detail === 'object' && typeof e.detail.detail === 'string') {
                msg = e.detail.detail;
                if (e.detail.error === 'invalid_recipient') {
                    setThread((prev) => prev
                        ? { ...prev, conversation: { ...(prev.conversation || {}), is_unreplyable: true } }
                        : prev);
                }
            } else if (typeof e?.detail === 'string') {
                msg = e.detail;
            } else if (typeof e?.message === 'string') {
                msg = e.message;
            }
            toastError(msg);
        } finally {
            setSending(false);
        }
    };

    // Phase 2 — file d'attente de médias. Un message par fichier (comportement
    // WhatsApp natif). Le premier reçoit la caption `text`, les suivants
    // partent sans caption. En cas d'échec en cours de batch, on stoppe et
    // on remonte l'erreur — les fichiers déjà envoyés restent envoyés.
    const submitMediaBatch = async ({ text, channelId }) => {
        if (!selectedId || attachedFiles.length === 0) return;
        if (!channelId) {
            toastError('Cette conversation ne supporte pas les médias (canal legacy).');
            return;
        }
        setSending(true);
        try {
            const files = attachedFiles.slice();
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const kind = kindFromFile(file);
                setUploadState({ index: i, total: files.length, percent: 0 });
                let uploaded;
                try {
                    uploaded = await messagerieApi.uploadMedia({
                        conversationId: selectedId,
                        kind,
                        file,
                        onProgress: (pct) => setUploadState({ index: i, total: files.length, percent: pct }),
                    });
                } catch (e) {
                    toastError(e?.detail || e?.message || 'Envoi du fichier impossible');
                    return; // stop batch
                }
                const payload = {
                    conversation_id: selectedId,
                    channel_id: channelId,
                    message_type: kind,
                    media_file: {
                        r2_key: uploaded.r2_key,
                        mime_type: uploaded.mime_type,
                        file_size: uploaded.file_size,
                        filename: uploaded.filename,
                    },
                };
                if (i === 0 && text) payload.text_content = text;
                let sent;
                try {
                    sent = await messagerieApi.send(payload, { silent: true });
                } catch (e) {
                    toastError(e?.detail || e?.message || 'Envoi Whapi impossible');
                    return;
                }
                setThread((prev) => prev ? { ...prev, items: [...prev.items, sent.message] } : prev);
                setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                    ...c,
                    last_message_preview: (i === 0 && text ? text : `[${kind}]`).slice(0, 100),
                    last_message_timestamp: sent.message.timestamp,
                } : c) : prev);
            }
            setDraft('');
            setAttachedFiles([]);
        } finally {
            setUploadState(null);
            setSending(false);
        }
    };

    // Phase B.3.2 — Whapi = source de vérité unique. Clic emoji :
    //   1. POST /react (serveur appelle Whapi, aucune écriture DB) ;
    //   2. fermeture immédiate du picker, PAS d'update optimiste ;
    //   3. 2 refetches ciblés (1 s + 3 s) pour récupérer l'état canonique
    //      posé par le webhook `messages_reactions` → pas de "polling"
    //      continu, strictement événementiel (post-click uniquement).
    // Erreur 4xx/5xx → toast en français, aucun changement local.
    const reactToMessage = React.useCallback(async (message, emoji) => {
        if (!message || !message.id || !selectedId) return;
        try {
            await messagerieApi.react(message.id, emoji || '');
        } catch (e) {
            toastError(e?.detail || e?.message || 'Impossible d\u2019envoyer la r\u00e9action, r\u00e9essayez');
            return;
        }
        const refetch = async () => {
            try {
                const res = await messagerieApi.getMessages(selectedId, { page_size: 200 });
                setThread((prev) => {
                    if (!prev || prev.conversation?.id !== selectedId) return prev;
                    return res;
                });
            } catch (_e) { /* silencieux */ }
        };
        setTimeout(refetch, 1000);
        setTimeout(refetch, 3000);
    }, [selectedId]);

    // Phase 3.1 — retry d'un message "failed". Réutilise le même document
    // (retry_message_id) : le backend le repasse en `sending` puis `sent`
    // ou `failed`. Zéro doublon.
    const retryMessage = React.useCallback(async (message) => {
        if (!message || !message.id) return;
        // Optimistic UI : repasse le message en "sending" immédiatement.
        setThread((prev) => prev
            ? { ...prev, items: prev.items.map((m) => m.id === message.id ? { ...m, status: 'sending' } : m) }
            : prev);
        try {
            const res = await messagerieApi.send(
                { conversation_id: selectedId, body: message.body || '', retry_message_id: message.id },
                { silent: true },
            );
            setThread((prev) => prev
                ? { ...prev, items: prev.items.map((m) => m.id === res.message.id ? res.message : m) }
                : prev);
            setConversations((prev) => prev ? prev.map((c) => c.id === selectedId ? {
                ...c,
                last_message_preview: (message.body || '').slice(0, 100),
                last_message_timestamp: res.message.timestamp,
            } : c) : prev);
        } catch (e) {
            // Reflet local de l'échec (le backend a déjà marqué "failed").
            setThread((prev) => prev
                ? { ...prev, items: prev.items.map((m) => m.id === message.id ? { ...m, status: 'failed' } : m) }
                : prev);
            let msg = 'Renvoi impossible';
            if (e?.detail && typeof e.detail === 'object' && typeof e.detail.detail === 'string') {
                msg = e.detail.detail;
            } else if (typeof e?.detail === 'string') {
                msg = e.detail;
            }
            toastError(msg);
        }
    }, [selectedId]);

    return (
        <div
            data-testid="messagerie-page"
            className="h-[calc(100vh-56px)] bg-[#F5F6FA] flex overflow-hidden flex-col"
        >
            {!bridgeActive ? (
                <div
                    data-testid="messagerie-bridge-inactive-banner"
                    className="shrink-0 bg-[#FEF3C7] border-b border-[#FDE68A] px-4 py-2 text-[12px] text-[#92400E] font-medium text-center"
                >
                    Les conversations du bridge legacy sont en lecture seule. Utilisez un canal Whapi pour répondre.
                </div>
            ) : null}
            <div className="flex-1 flex overflow-hidden">
            <ConversationList
                conversations={conversations}
                loading={loadingConvs}
                selectedId={selectedId}
                onSelect={handleSelect}
                search={search}
                onSearchChange={setSearch}
                canFilterByOwner={canFilterByOwner}
                bridgeStatus={bridgeStatus}
                isAdmin={isAdmin}
                channels={channels}
                channelFilter={channelFilter}
                onChannelFilterChange={setChannelFilter}
                userAssignedChannelId={user?.assigned_channel_id || null}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
            />
            <ThreadPane
                thread={thread}
                loading={loadingThread}
                onSend={submitMessage}
                onRetry={retryMessage}
                onReact={reactToMessage}
                sending={sending}
                draft={draft}
                setDraft={setDraft}
                currentUser={user}
                attachedFiles={attachedFiles}
                setAttachedFiles={setAttachedFiles}
                uploadState={uploadState}
                isAdmin={isAdmin}
                channels={channels}
                onOpenParticipants={openParticipants}
                groupInfo={groupInfo}
                onReassign={async (agent_user_id) => {
                    if (!selectedId) return;
                    try {
                        const res = await messagerieApi.reassign(selectedId, agent_user_id);
                        setThread((prev) => prev ? { ...prev, conversation: res.conversation } : prev);
                    } catch (e) {
                        toastError(e?.detail || e?.message || 'Réassignation impossible');
                    }
                }}
            />
            {/* Phase 4 — Panneau latéral des participants (groupes uniquement) */}
            {participantsOpen ? (
                <ParticipantsPanel
                    group={groupInfo}
                    loading={loadingGroup}
                    onClose={() => setParticipantsOpen(false)}
                />
            ) : null}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// Phase 19 — bridge status indicator (dot + tooltip label)
// -----------------------------------------------------------------------
function BridgeStatusDot({ status }) {
    const map = {
        ready:       { color: '#10B981', label: 'Bridge actif' },
        waiting:     { color: '#F59E0B', label: 'Bridge en attente de connexion' },
        unreachable: { color: '#EF4444', label: 'Bridge injoignable' },
    };
    const conf = map[status];
    if (!conf) {
        return (
            <span
                data-testid="bridge-status-dot"
                data-status="loading"
                title="Vérification du bridge…"
                aria-label="Vérification du bridge"
                className="inline-block w-2 h-2 rounded-full bg-[#D1D5DB]"
            />
        );
    }
    return (
        <span
            data-testid="bridge-status-dot"
            data-status={status}
            title={conf.label}
            aria-label={conf.label}
            className="inline-flex items-center gap-1.5"
        >
            <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: conf.color }}
            />
            <span
                data-testid="bridge-status-label"
                className="text-[10px] font-medium leading-none"
                style={{ color: conf.color }}
            >
                {conf.label}
            </span>
        </span>
    );
}

// -----------------------------------------------------------------------
// Left panel — conversation list
// -----------------------------------------------------------------------
// Phase 3 — Widget statut canaux (admin) : 3 pastilles colorées + tooltip.
// Agent : une seule pastille (son canal). Cliquer sur une pastille filtre
// la liste (admin uniquement).
function ChannelStatusWidget({ channels, userAssignedChannelId, isAdmin, channelFilter, onFilter }) {
    if (!isAdmin && !userAssignedChannelId) return null;
    const items = isAdmin
        ? (channels || [])
        : (userAssignedChannelId
            ? [(channels || []).find((c) => c.channel_id === userAssignedChannelId) || { channel_id: userAssignedChannelId }]
            : []);
    return (
        <div data-testid="messagerie-channels-widget" className="flex items-center gap-1.5">
            {items.map((c) => {
                const color = c?.status === 'connected' ? '#10B981'
                    : c?.status === 'qr_required' ? '#F59E0B'
                    : '#EF4444';
                const label = `${c.channel_id}${c.commercial_name ? ' (' + c.commercial_name + ')' : ''}`;
                const active = isAdmin && channelFilter === c.channel_id;
                return (
                    <button
                        key={c.channel_id}
                        type="button"
                        data-testid="messagerie-channel-dot"
                        data-channel-id={c.channel_id}
                        data-status={c?.status || 'unknown'}
                        title={label + ' — ' + (c?.status || 'inconnu')}
                        onClick={isAdmin && onFilter ? () => onFilter(active ? null : c.channel_id) : undefined}
                        disabled={!isAdmin}
                        className={
                            'inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-medium border ' +
                            (active
                                ? 'bg-[#4B3FD8]/10 border-[#4B3FD8] text-[#4B3FD8]'
                                : 'bg-white border-[#E5E7EB] text-[#1A1D2E]') +
                            (isAdmin ? ' hover:bg-[#F5F6FA]' : ' cursor-default')
                        }
                    >
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                        {c.channel_id}
                    </button>
                );
            })}
        </div>
    );
}

function ConversationList({
    conversations, loading, selectedId, onSelect,
    search, onSearchChange, canFilterByOwner, bridgeStatus,
    isAdmin, channels, channelFilter, onChannelFilterChange, userAssignedChannelId,
    typeFilter, onTypeFilterChange,
}) {
    return (
        <aside
            data-testid="messagerie-list"
            className="w-[30%] min-w-[280px] max-w-[420px] shrink-0 h-full border-r border-[#E5E7EB] bg-white flex flex-col"
        >
            <div className="p-3 border-b border-[#E5E7EB] space-y-2 shrink-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[#1A1D2E]">
                        <MessageSquare size={18} strokeWidth={1.75} />
                        <h1 className="text-sm font-semibold">Messagerie</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <ChannelStatusWidget
                            channels={channels}
                            userAssignedChannelId={userAssignedChannelId}
                            isAdmin={isAdmin}
                            channelFilter={channelFilter}
                            onFilter={onChannelFilterChange}
                        />
                        <BridgeStatusDot status={bridgeStatus} />
                    </div>
                </div>
                {isAdmin ? (
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] uppercase tracking-wide text-[#6B7280] shrink-0">Canal</label>
                        <select
                            data-testid="messagerie-channel-filter"
                            value={channelFilter || ''}
                            onChange={(e) => onChannelFilterChange && onChannelFilterChange(e.target.value || null)}
                            className="flex-1 h-7 px-2 text-xs rounded-md border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                        >
                            <option value="">Tous les canaux</option>
                            {(channels || []).map((c) => (
                                <option key={c.channel_id} value={c.channel_id}>
                                    {c.channel_id}{c.commercial_name ? ` — ${c.commercial_name}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
                {/* Phase 4 — Toggle type conversation (Tous / Individuels / Groupes) */}
                <div
                    data-testid="messagerie-type-toggle"
                    role="tablist"
                    className="inline-flex h-7 w-full rounded-md border border-[#E5E7EB] bg-white p-0.5"
                >
                    {[
                        { value: 'all',        label: 'Tous',        icon: null },
                        { value: 'individual', label: 'Individuels', icon: UserIcon },
                        { value: 'group',      label: 'Groupes',     icon: Users },
                    ].map((opt) => {
                        const Icon = opt.icon;
                        const active = (typeFilter || 'all') === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                data-testid={`messagerie-type-toggle-${opt.value}`}
                                data-active={active ? 'true' : 'false'}
                                onClick={() => onTypeFilterChange && onTypeFilterChange(opt.value)}
                                className={
                                    'flex-1 h-full inline-flex items-center justify-center gap-1 rounded text-[11px] font-medium transition-colors ' +
                                    (active
                                        ? 'bg-[#4B3FD8] text-white'
                                        : 'text-[#6B7280] hover:bg-[#F5F6FA]')
                                }
                            >
                                {Icon ? <Icon size={12} strokeWidth={2} /> : null}
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                <div className="relative">
                    <Search
                        size={14}
                        strokeWidth={1.75}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none"
                    />
                    <input
                        data-testid="messagerie-search"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Rechercher un nom ou un numéro…"
                        className="w-full h-8 pl-8 pr-3 text-sm rounded-md border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                    />
                </div>
            </div>
            <ScrollAreaSmart data-testid="messagerie-list-scroll" className="flex-1">
                {loading && <ListSkeleton />}
                {!loading && conversations && conversations.length === 0 && (
                    <div
                        data-testid="messagerie-list-empty"
                        className="p-8 text-center text-sm text-[#6B7280]"
                    >
                        Aucune conversation pour le moment. Les messages WhatsApp de vos clients apparaîtront ici.
                    </div>
                )}
                {!loading && conversations && conversations.length > 0 && (
                    <ul className="divide-y divide-[#F3F4F6]">
                        {conversations.map((c) => (
                            <ConversationRow
                                key={c.id}
                                conv={c}
                                selected={c.id === selectedId}
                                onSelect={() => onSelect(c.id)}
                                showOwner={canFilterByOwner}
                            />
                        ))}
                    </ul>
                )}
            </ScrollAreaSmart>
        </aside>
    );
}

function ConversationRow({ conv, selected, onSelect, showOwner }) {
    const isGroup = conv.type === 'group';
    const displayName = isGroup
        ? (conv.group_name || conv.name || conv.contact_name || 'Groupe WhatsApp')
        : (conv.contact_name || conv.phone_number || '—');
    const previewCap = (conv.last_message_preview || '').slice(0, 60);
    // Clé stable pour la couleur d'avatar : wa_id > phone > name.
    const avatarKey = conv.wa_id || conv.group_id || conv.phone_number || displayName;
    return (
        <li>
            <button
                type="button"
                data-testid="messagerie-conv-row"
                data-conv-id={conv.id}
                data-conv-type={isGroup ? 'group' : 'individual'}
                data-selected={selected ? 'true' : 'false'}
                onClick={onSelect}
                className={
                    'w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ' +
                    (selected ? 'bg-[#4B3FD8]/8' : 'bg-white hover:bg-[#F5F6FA]')
                }
            >
                <div className="relative shrink-0">
                    <Avatar
                        name={displayName}
                        phone={conv.phone_number}
                        waId={avatarKey}
                        r2Key={conv.avatar_r2_key}
                        testId="messagerie-conv-avatar"
                    />
                    {isGroup ? (
                        <span
                            data-testid="messagerie-conv-group-badge"
                            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center text-[#4B3FD8]"
                            title="Groupe WhatsApp"
                            aria-label="Groupe WhatsApp"
                        >
                            <Users size={9} strokeWidth={2.25} />
                        </span>
                    ) : null}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-[#1A1D2E] truncate">{displayName}</span>
                        <span className="text-[10px] text-[#6B7280] shrink-0 tabular-nums">
                            {relativeFromISO(conv.last_message_timestamp)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-xs text-[#6B7280] truncate">{previewCap}</span>
                        {conv.unread_count > 0 && (
                            <span
                                data-testid="messagerie-conv-unread"
                                className="ml-2 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-semibold bg-[#4B3FD8] text-white shrink-0"
                            >
                                {conv.unread_count}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                        {showOwner && conv.owner_cc && (
                            <span
                                data-testid="messagerie-conv-owner"
                                className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-medium border border-[#E5E7EB] text-[#6B7280]"
                            >
                                {conv.owner_cc}
                            </span>
                        )}
                    </div>
                </div>
            </button>
        </li>
    );
}

function ListSkeleton() {
    return (
        <ul className="divide-y divide-[#F3F4F6]" data-testid="messagerie-list-skeleton">
            {Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="px-3 py-3 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#F3F4F6] animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                        <div className="h-3 rounded bg-[#F3F4F6] w-1/2 animate-pulse" />
                        <div className="h-3 rounded bg-[#F3F4F6] w-3/4 animate-pulse" />
                    </div>
                </li>
            ))}
        </ul>
    );
}

// -----------------------------------------------------------------------
// Right panel — thread + composer
// -----------------------------------------------------------------------
function ThreadPane({ thread, loading, onSend, onRetry, onReact, sending, draft, setDraft, currentUser, attachedFiles, setAttachedFiles, uploadState, isAdmin, channels, onReassign, onOpenParticipants, groupInfo }) {
    if (!thread) {
        return (
            <section
                data-testid="messagerie-thread-empty"
                className="flex-1 h-full flex items-center justify-center text-sm text-[#6B7280] bg-[#F5F6FA]"
            >
                {loading ? 'Chargement…' : 'Sélectionnez une conversation'}
            </section>
        );
    }
    const conv = thread.conversation || {};
    const messages = thread.items || [];
    const canSeeOwner = currentUser && (currentUser.role === ROLES.SUPER_ADMIN || currentUser.role === ROLES.MANAGER);
    // Phase 19.3 — JID business/channel → composer désactivé.
    const isUnreplyable = conv.is_unreplyable === true;
    const unreplyableTooltip = "Ce contact n'a pas de numéro WhatsApp standard. Répondez depuis le téléphone bridge directement.";
    // Phase 4 — groupe : header enrichi + bouton participants.
    const isGroup = conv.type === 'group';
    const groupName = groupInfo?.name || conv.group_name || conv.contact_name || 'Groupe WhatsApp';
    const groupDescription = groupInfo?.description || conv.description;
    const groupParticipantCount = groupInfo?.participant_count;
    const groupAvatarKey = conv.wa_id || conv.group_id;
    const displayLabel = isGroup ? groupName : (conv.contact_name || conv.phone_number || conv.whatsapp_jid || '—');

    return (
        <section data-testid="messagerie-thread" className="flex-1 h-full flex flex-col bg-[#F5F6FA]">
            {/* Header */}
            <header className="h-14 shrink-0 bg-white border-b border-[#E5E7EB] px-4 flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-center gap-2.5">
                    {isGroup ? (
                        <Avatar
                            name={groupName}
                            waId={groupAvatarKey}
                            r2Key={groupInfo?.avatar_r2_key || conv.avatar_r2_key}
                            sizeClass="w-8 h-8"
                            textClass="text-[11px]"
                            testId="messagerie-thread-avatar"
                        />
                    ) : null}
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1A1D2E] truncate flex items-center gap-1.5">
                            {isGroup ? <Users size={13} strokeWidth={2} className="text-[#4B3FD8] shrink-0" /> : null}
                            <span data-testid="messagerie-thread-title" className="truncate">{displayLabel}</span>
                        </div>
                        {isGroup && groupDescription ? (
                            <GroupDescription text={groupDescription} />
                        ) : (!isGroup && conv.contact_name && (conv.phone_number || conv.whatsapp_jid)) ? (
                            <div className="text-[11px] text-[#6B7280]">
                                {conv.phone_number || conv.whatsapp_jid}
                            </div>
                        ) : isGroup && typeof groupParticipantCount === 'number' ? (
                            <div className="text-[11px] text-[#6B7280]">
                                {groupParticipantCount} participant{groupParticipantCount > 1 ? 's' : ''}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {isUnreplyable && (
                        <span
                            data-testid="messagerie-thread-unreplyable"
                            title={unreplyableTooltip}
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
                        >
                            Non répondable
                        </span>
                    )}
                    {conv.channel_id ? (
                        <span
                            data-testid="messagerie-thread-channel"
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium border border-[#4B3FD8] text-[#4B3FD8] bg-[#4B3FD8]/5"
                        >
                            {conv.channel_id}
                        </span>
                    ) : null}
                    {canSeeOwner && conv.owner_cc && (
                        <span
                            data-testid="messagerie-thread-owner"
                            className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium border border-[#E5E7EB] text-[#6B7280]"
                        >
                            {conv.owner_cc}
                        </span>
                    )}
                    {/* Phase 4 — bouton "Voir participants" (groupes) */}
                    {isGroup ? (
                        <button
                            type="button"
                            data-testid="messagerie-thread-participants-btn"
                            onClick={() => onOpenParticipants && onOpenParticipants()}
                            className="inline-flex items-center gap-1 h-6 px-2 rounded border border-[#E5E7EB] text-[11px] text-[#1A1D2E] hover:bg-[#F5F6FA]"
                            title="Voir les participants"
                        >
                            <Users size={12} strokeWidth={2} />
                            Participants
                        </button>
                    ) : null}
                    {/* Phase 3 — Admin: dropdown de réassignation. */}
                    {isAdmin ? (
                        <select
                            data-testid="messagerie-thread-reassign"
                            value={conv.assigned_agent_id || ''}
                            onChange={(e) => onReassign && onReassign(e.target.value || null)}
                            className="h-6 max-w-[180px] px-1.5 text-[11px] rounded border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                            title="Réassigner à un agent"
                        >
                            <option value="">— Non assigné —</option>
                            {(channels || []).filter((c) => c.agent).map((c) => (
                                <option key={c.channel_id} value={c.agent.id}>
                                    {c.channel_id} · {c.agent.display_name || c.agent.email}
                                </option>
                            ))}
                        </select>
                    ) : null}
                </div>
            </header>

            {/* Messages */}
            <MessageStream messages={messages} loading={loading} onRetry={onRetry} onReact={onReact} isGroup={isGroup} conversation={conv} />

            {/* Composer — désactivé si conv non-répondable (JID) */}
            {isUnreplyable ? (
                <div
                    data-testid="messagerie-composer-disabled"
                    title={unreplyableTooltip}
                    className="shrink-0 border-t border-[#E5E7EB] bg-white p-3"
                >
                    <div className="h-9 px-3 rounded-lg bg-[#F5F6FA] border border-dashed border-[#E5E7EB] text-xs text-[#6B7280] flex items-center">
                        {unreplyableTooltip}
                    </div>
                </div>
            ) : (
                <MessageComposer
                    thread={thread}
                    draft={draft}
                    setDraft={setDraft}
                    onSend={onSend}
                    sending={sending}
                    attachedFiles={attachedFiles || []}
                    setAttachedFiles={setAttachedFiles || (() => {})}
                    uploadState={uploadState}
                />
            )}
        </section>
    );
}

// Phase 4 — description groupe : tronquée puis expansible.
function GroupDescription({ text }) {
    const [expanded, setExpanded] = React.useState(false);
    const short = (text || '').length > 60 && !expanded;
    if (!text) return null;
    return (
        <button
            type="button"
            data-testid="messagerie-thread-group-description"
            onClick={() => setExpanded((v) => !v)}
            className="block text-left text-[11px] text-[#6B7280] max-w-[380px] hover:text-[#4B3FD8]"
            title={expanded ? 'Réduire' : 'Voir la description complète'}
        >
            {short ? `${text.slice(0, 60)}…` : text}
        </button>
    );
}

// Phase 2 — composer avec file picker + drag-drop.
// Contraintes UX :
// - Un fichier par message (WhatsApp natif) : plusieurs fichiers droppés →
//   file d'attente traitée séquentiellement côté MessageriePage.
// - Barre de progression pendant l'upload.
// - Bouton Envoyer actif si (draft non vide) OU (au moins 1 fichier attaché).
// - Convs legacy (sans `channel_id`) : le bouton pièce jointe est désactivé
//   avec un tooltip explicatif — le bridge whatsapp-web.js ne gère pas les
//   médias en Phase 2.
function MessageComposer({ thread, draft, setDraft, onSend, sending, attachedFiles, setAttachedFiles, uploadState }) {
    const conv = thread?.conversation || {};
    const isWhapi = !!conv.channel_id;
    const composerBadge = isWhapi ? conv.channel_id : null;
    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef(null);

    const addFiles = (fileList) => {
        const arr = Array.from(fileList || []);
        if (!arr.length) return;
        // Filtre les fichiers vides.
        setAttachedFiles((prev) => [...prev, ...arr.filter((f) => f && f.size > 0)]);
    };
    const removeFile = (idx) => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

    const onPick = (e) => {
        addFiles(e.target.files);
        e.target.value = '';
    };
    const onDragOver = (e) => {
        if (!isWhapi) return;
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (!isWhapi) return;
        addFiles(e.dataTransfer && e.dataTransfer.files);
    };

    const canSubmit = !sending && ((draft.trim().length > 0) || attachedFiles.length > 0);

    return (
        <form
            data-testid="messagerie-composer"
            data-whapi={isWhapi ? 'true' : 'false'}
            onSubmit={(e) => { e.preventDefault(); onSend(); }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={
                'shrink-0 border-t border-[#E5E7EB] bg-white p-3 space-y-2 relative ' +
                (dragging ? 'ring-2 ring-[#4B3FD8] ring-offset-0' : '')
            }
        >
            {dragging ? (
                <div
                    data-testid="messagerie-composer-dropzone"
                    className="absolute inset-0 bg-[#4B3FD8]/8 flex items-center justify-center text-sm text-[#4B3FD8] font-medium pointer-events-none rounded"
                >
                    Déposez vos fichiers pour les envoyer
                </div>
            ) : null}
            {attachedFiles.length > 0 ? (
                <ul
                    data-testid="messagerie-composer-attachments"
                    className="flex flex-wrap gap-2"
                >
                    {attachedFiles.map((f, i) => (
                        <li
                            key={`${f.name}-${i}`}
                            data-testid="messagerie-composer-attachment"
                            data-filename={f.name}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F5F6FA] border border-[#E5E7EB] text-xs text-[#1A1D2E]"
                        >
                            {kindFromFile(f) === 'image' ? <ImageIcon size={12} /> :
                             kindFromFile(f) === 'video' ? <VideoIcon size={12} /> :
                             kindFromFile(f) === 'voice' ? <Mic size={12} /> :
                             <FileText size={12} />}
                            <span className="max-w-[160px] truncate">{f.name}</span>
                            <span className="text-[10px] text-[#6B7280] tabular-nums">
                                {(f.size / 1024).toFixed(1)} Ko
                            </span>
                            <button
                                type="button"
                                data-testid="messagerie-composer-attachment-remove"
                                onClick={() => removeFile(i)}
                                disabled={sending}
                                className="text-[#6B7280] hover:text-[#EF4444] disabled:opacity-40"
                                aria-label="Retirer"
                            >
                                <CloseIcon size={12} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {uploadState ? (
                <div
                    data-testid="messagerie-composer-progress"
                    data-percent={uploadState.percent}
                    className="flex items-center gap-2 text-[11px] text-[#6B7280]"
                >
                    <span className="tabular-nums shrink-0">
                        {uploadState.index + 1}/{uploadState.total}
                    </span>
                    <div className="h-1.5 flex-1 rounded-full bg-[#F3F4F6] overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] transition-all"
                            style={{ width: `${uploadState.percent}%` }}
                        />
                    </div>
                    <span className="tabular-nums shrink-0">{uploadState.percent}%</span>
                </div>
            ) : null}
            <div className="flex items-center gap-2">
                {composerBadge ? (
                    <span
                        data-testid="messagerie-composer-channel-badge"
                        data-channel-id={composerBadge}
                        className="inline-flex items-center h-6 px-2 rounded text-[10px] font-medium border border-[#4B3FD8] text-[#4B3FD8] bg-[#4B3FD8]/5"
                        title="Canal d'envoi"
                    >
                        Envoi : {composerBadge}
                    </span>
                ) : null}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    data-testid="messagerie-composer-file-input"
                    onChange={onPick}
                    disabled={!isWhapi || sending}
                />
                <button
                    type="button"
                    data-testid="messagerie-composer-attach"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={!isWhapi || sending}
                    title={isWhapi
                        ? 'Joindre un fichier (image, audio, vidéo, document)'
                        : 'Les médias ne sont pas supportés sur cette conversation.'}
                    aria-label="Joindre un fichier"
                    className="h-9 w-9 shrink-0 rounded-lg border border-[#E5E7EB] bg-white text-[#4B3FD8] inline-flex items-center justify-center hover:bg-[#F5F6FA] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Paperclip size={16} strokeWidth={1.75} />
                </button>
                <input
                    data-testid="messagerie-composer-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={attachedFiles.length ? 'Ajouter un message (facultatif)…' : 'Écrivez un message…'}
                    disabled={sending}
                    className="flex-1 h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8] disabled:opacity-60"
                />
                <button
                    type="submit"
                    data-testid="messagerie-composer-send"
                    disabled={!canSubmit}
                    className="h-9 px-3 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                    <Send size={14} strokeWidth={2} />
                    Envoyer
                </button>
            </div>
        </form>
    );
}

function MessageStream({ messages, loading, onRetry, onReact, isGroup, conversation }) {
    const scrollerRef = React.useRef(null);
    const [pinnedBottom, setPinnedBottom] = React.useState(true);
    const [hasNew, setHasNew] = React.useState(false);
    const lastCountRef = React.useRef(messages.length);

    // Phase A.2 — map pour la citation (reply-to). Clé = identifiant Whapi
    // d'un message déjà chargé (whapi_message_id ou external_message_id).
    // Recalculée à chaque changement de la liste messages — O(n), trivial.
    const whapiIdMap = React.useMemo(() => {
        const m = new Map();
        for (const msg of messages) {
            if (msg.whapi_message_id) m.set(msg.whapi_message_id, msg);
            if (msg.external_message_id && !m.has(msg.external_message_id)) {
                m.set(msg.external_message_id, msg);
            }
        }
        return m;
    }, [messages]);

    const scrollToBottom = React.useCallback((behavior = 'smooth') => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
        setPinnedBottom(true);
        setHasNew(false);
    }, []);

    // Initial scroll to bottom (jump).
    React.useEffect(() => {
        scrollToBottom('auto');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset when the conversation loads for the first time.
    React.useEffect(() => {
        if (messages.length && lastCountRef.current === 0) {
            scrollToBottom('auto');
        }
        if (messages.length > lastCountRef.current) {
            if (pinnedBottom) scrollToBottom('smooth');
            else setHasNew(true);
        }
        lastCountRef.current = messages.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    const onScroll = (e) => {
        const el = e.currentTarget;
        const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const atBottom = dist < 80;
        setPinnedBottom(atBottom);
        if (atBottom) setHasNew(false);
    };

    // Phase 3.1 — injecte les séparateurs de date (Africa/Casablanca) en
    // parcourant les messages triés. `nowIso` capturé une fois par render :
    // suffisant pour la stabilité visuelle d'un même rendu.
    // Phase 4 — n'affiche le nom d'expéditeur au-dessus de la bulle QUE si :
    //   * la conv est un groupe
    //   * ET le message est entrant
    //   * ET l'expéditeur diffère du précédent message affiché
    // (comportement WhatsApp natif : on regroupe visuellement les messages
    // consécutifs d'un même expéditeur).
    const nowIso = new Date().toISOString();
    const rendered = [];
    let lastDayKey = null;
    let lastSenderKey = null;
    for (const m of messages) {
        const key = casaDayKey(m.timestamp);
        if (key && key !== lastDayKey) {
            rendered.push(
                <DateSeparator key={`sep-${key}`} label={formatSeparatorLabel(m.timestamp, nowIso)} />,
            );
            lastDayKey = key;
            lastSenderKey = null; // reset au changement de jour
        }
        if (m.direction === 'system' || m.message_type === 'system') {
            rendered.push(<SystemMessage key={m.id} message={m} />);
            lastSenderKey = null;
            continue;
        }
        const isInbound = m.direction === 'inbound';
        const showSender = isGroup && isInbound && (m.sender_wa_id || m.from_phone || m.from);
        const currentSenderKey = m.sender_wa_id || m.from_phone || m.from || null;
        const senderChanged = showSender && currentSenderKey !== lastSenderKey;
        rendered.push(
            <MessageBubble
                key={m.id}
                message={m}
                onRetry={onRetry}
                onReact={onReact}
                isGroup={isGroup}
                showSender={senderChanged}
                repliedTo={m.reply_to_whapi_id ? (whapiIdMap.get(m.reply_to_whapi_id) || null) : null}
                conversation={conversation}
            />
        );
        if (isInbound) lastSenderKey = currentSenderKey;
        else lastSenderKey = null; // outbound bubble n'entre pas dans le regroupement
    }

    return (
        <div className="relative flex-1 min-h-0">
            <div
                ref={scrollerRef}
                data-testid="messagerie-thread-scroll"
                onScroll={onScroll}
                className="absolute inset-0 overflow-y-auto p-4 space-y-2"
            >
                {loading && messages.length === 0 && (
                    <div className="text-center text-xs text-[#6B7280]">Chargement…</div>
                )}
                {!loading && messages.length === 0 && (
                    <div className="text-center text-xs text-[#6B7280]">Aucun message dans cette conversation.</div>
                )}
                {rendered}
            </div>
            {hasNew && (
                <button
                    type="button"
                    data-testid="messagerie-new-messages-btn"
                    onClick={() => scrollToBottom('smooth')}
                    className="absolute left-1/2 -translate-x-1/2 bottom-3 h-7 px-3 rounded-full bg-[#4B3FD8] text-white text-xs font-medium inline-flex items-center gap-1.5 shadow-md"
                >
                    <ArrowDown size={12} strokeWidth={2} />
                    Nouveaux messages
                </button>
            )}
        </div>
    );
}

// Phase 4 — message système centré et atténué (WhatsApp natif).
// Pas de bulle expéditeur ni d'avatar ; texte pré-calculé côté worker.
function SystemMessage({ message }) {
    return (
        <div
            data-testid="messagerie-msg-system"
            data-system-event={message.system_event_type || 'unknown'}
            className="my-2 flex items-center justify-center"
        >
            <span className="px-3 py-1 rounded-full bg-white/70 border border-[#E5E7EB] text-[11px] font-medium text-[#6B7280] shadow-sm max-w-[80%] text-center">
                {message.body || '…'}
            </span>
        </div>
    );
}

function DateSeparator({ label }) {
    return (
        <div
            data-testid="messagerie-date-separator"
            data-label={label}
            className="my-3 flex items-center justify-center"
        >
            <span className="px-2.5 py-0.5 rounded-full bg-white border border-[#E5E7EB] text-[10px] font-medium text-[#6B7280] shadow-sm">
                {label}
            </span>
        </div>
    );
}

// Rend un message body avec markdown WhatsApp + linkify (React nodes → XSS-safe).
function MessageBody({ text }) {
    const parts = React.useMemo(() => parseWhatsAppMarkdown(text || ''), [text]);
    if (parts.length === 0) return null;
    return (
        <Linkify as="span" options={LINKIFY_OPTS}>
            {parts.map((p, i) => {
                if (p.type === 'bold') return <strong key={i}>{p.text}</strong>;
                if (p.type === 'italic') return <em key={i}>{p.text}</em>;
                if (p.type === 'strike') return <s key={i}>{p.text}</s>;
                return <React.Fragment key={i}>{p.text}</React.Fragment>;
            })}
        </Linkify>
    );
}

function OutboundStatusIcon({ status, onRetry }) {
    if (status === 'sending' || !status) {
        return (
            <span
                data-testid="messagerie-msg-status"
                data-status="sending"
                title="Envoi en cours…"
                aria-label="Envoi en cours"
                className="inline-flex items-center text-[#6B7280]"
            >
                <Clock size={12} strokeWidth={2} />
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <button
                type="button"
                data-testid="messagerie-msg-retry"
                data-status="failed"
                onClick={onRetry}
                title="Échec — cliquez pour réessayer"
                aria-label="Échec — cliquez pour réessayer"
                className="inline-flex items-center gap-0.5 text-[#EF4444] hover:opacity-80"
            >
                <AlertTriangle size={12} strokeWidth={2} />
                <RotateCw size={10} strokeWidth={2} />
            </button>
        );
    }
    if (status === 'delivered') {
        return (
            <span
                data-testid="messagerie-msg-status"
                data-status="delivered"
                title="Distribué"
                aria-label="Distribué"
                className="inline-flex items-center text-[#6B7280]"
            >
                <CheckCheck size={12} strokeWidth={2} />
            </span>
        );
    }
    if (status === 'read') {
        return (
            <span
                data-testid="messagerie-msg-status"
                data-status="read"
                title="Lu"
                aria-label="Lu"
                className="inline-flex items-center text-blue-500"
            >
                <CheckCheck size={12} strokeWidth={2} />
            </span>
        );
    }
    // "sent" (default fallback pour les messages historiques sans status).
    return (
        <span
            data-testid="messagerie-msg-status"
            data-status="sent"
            title="Envoyé"
            aria-label="Envoyé"
            className="inline-flex items-center text-[#6B7280]"
        >
            <Check size={12} strokeWidth={2} />
        </span>
    );
}

// Phase 2 — rendu du média selon son `kind`. Le `r2_key` sert de src
// d'accès (endpoint /messagerie/media/{r2_key:path} → 302 presigned URL).
// Phase 5 — placeholder "Média expiré" quand le cron a supprimé l'objet R2.
// Les messages historiques sans r2_key gardent le placeholder legacy.
function MediaAttachment({ media }) {
    if (!media) return null;
    // Phase 5 — expiry cron a marqué le doc → afficher un placeholder clair.
    if (media.status === 'expired') {
        return (
            <span
                data-testid="messagerie-media-expired"
                title="Ce média a expiré et a été supprimé du stockage."
                className="inline-flex items-center gap-1.5 italic text-[#9CA3AF] text-xs px-2 py-1 rounded bg-[#F5F6FA] border border-dashed border-[#E5E7EB]"
            >
                <FileText size={12} strokeWidth={1.75} />
                Média expiré
            </span>
        );
    }
    const key = media.r2_key;
    if (!key) {
        // Legacy / not-yet-downloaded : garder le placeholder Phase 18.
        return <span className="italic text-[#6B7280]" data-testid="messagerie-media-placeholder">[Média]</span>;
    }
    const href = messagerieApi.mediaHref(key);
    const kind = (media.kind || '').toLowerCase();
    const filename = media.filename || (key.split('/').pop() || 'fichier');

    if (kind === 'image' || kind === 'sticker') {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="messagerie-media-image"
                data-r2-key={key}
                className="block max-w-[280px]"
            >
                <img
                    src={href}
                    alt={filename}
                    loading="lazy"
                    className="w-full h-auto rounded-lg border border-[#E5E7EB]"
                />
            </a>
        );
    }
    if (kind === 'video') {
        return (
            <video
                data-testid="messagerie-media-video"
                data-r2-key={key}
                src={href}
                controls
                preload="metadata"
                className="max-w-[280px] rounded-lg border border-[#E5E7EB]"
            />
        );
    }
    if (kind === 'voice' || kind === 'audio') {
        return (
            <div className="flex items-center gap-2 min-w-[220px]" data-testid="messagerie-media-audio" data-r2-key={key}>
                <Mic size={14} strokeWidth={2} className="text-[#4B3FD8] shrink-0" />
                <audio src={href} controls preload="metadata" className="h-8 w-full" />
                {media.duration ? (
                    <span className="text-[10px] text-[#6B7280] tabular-nums shrink-0">
                        {Math.round(Number(media.duration))}s
                    </span>
                ) : null}
            </div>
        );
    }
    // document (par défaut).
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            download={filename}
            data-testid="messagerie-media-document"
            data-r2-key={key}
            className="inline-flex items-center gap-2 max-w-[300px] px-2 py-1.5 rounded-md bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB]"
        >
            <FileText size={16} strokeWidth={1.75} className="text-[#4B3FD8] shrink-0" />
            <span className="truncate text-xs text-[#1A1D2E]">{filename}</span>
            {media.file_size ? (
                <span className="text-[10px] text-[#6B7280] shrink-0 tabular-nums">
                    {(Number(media.file_size) / 1024).toFixed(1)} Ko
                </span>
            ) : null}
        </a>
    );
}

// Phase A.2 — Bloc de citation (reply-to). Lookup client-side : si le message
// référencé n'est pas dans la liste chargée, afficher un placeholder italique.
function QuotedPreview({ repliedTo, isOutboundOwner, conversation }) {  // eslint-disable-line no-unused-vars
    if (!repliedTo) {
        return (
            <div
                data-testid="messagerie-msg-quote"
                data-quote-resolved="false"
                className="mb-1.5 pl-2 py-1 border-l-2 border-[#4B3FD8] text-[11px] italic text-[#9CA3AF]"
            >
                Message référencé
            </div>
        );
    }
    const quotedIsOut = repliedTo.direction === 'outbound';
    const senderLabel = quotedIsOut
        ? 'Vous'
        : (conversation && conversation.contact_name) || 'Contact';
    let preview;
    if (repliedTo.body && repliedTo.body.length) {
        preview = repliedTo.body.slice(0, 60);
        if (repliedTo.body.length > 60) preview += '…';
    } else if (repliedTo.has_media && repliedTo.media) {
        const kind = (repliedTo.media.kind || repliedTo.media.type || '').toLowerCase();
        if (kind.startsWith('image')) preview = '[Image]';
        else if (kind.startsWith('audio') || kind === 'voice' || kind === 'ptt') preview = '[Audio]';
        else if (kind.startsWith('video')) preview = '[Vidéo]';
        else preview = '[Document]';
    } else {
        preview = '[Message]';
    }
    return (
        <div
            data-testid="messagerie-msg-quote"
            data-quote-resolved="true"
            className="mb-1.5 pl-2 py-1 border-l-2 border-[#4B3FD8] bg-black/[0.03] rounded-sm"
        >
            <div className="text-[11px] font-medium text-[#4B3FD8] truncate">
                {senderLabel}
            </div>
            <div className="text-[12px] text-[#6B7280] truncate">
                {preview}
            </div>
        </div>
    );
}

// Phase B.3 — Pills de réactions (regroupées par emoji + compteur, tooltip
// liste des auteurs). Sous la bulle, alignement selon direction.
// Phase F2 — highlight bordure indigo si la propre réaction de l'agent est
// présente dans le groupe (une entrée avec `from_me: true`).
function ReactionPills({ reactions, align }) {
    const groups = React.useMemo(() => {
        const m = new Map();
        for (const r of reactions || []) {
            if (!r || !r.emoji) continue;
            const key = r.emoji;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(r);
        }
        return Array.from(m.entries()); // [[emoji, [reactions...]], ...]
    }, [reactions]);
    if (!groups.length) return null;
    return (
        <div
            data-testid="messagerie-msg-reactions"
            className={
                'flex flex-wrap gap-1 mt-1 ' + (align === 'end' ? 'justify-end' : 'justify-start')
            }
        >
            {groups.map(([emoji, list]) => {
                const authors = list
                    .map((r) => r.from_name || r.from_phone || 'Contact')
                    .join(', ');
                const isMine = list.some((r) => r && r.from_me === true);
                return (
                    <span
                        key={emoji}
                        data-mine={isMine ? 'true' : 'false'}
                        title={authors}
                        className={
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#1A1D2E] ' +
                            (isMine
                                ? 'bg-[#EEF0FE] border border-[#4B3FD8]'
                                : 'bg-gray-100 border border-[#E5E7EB]')
                        }
                    >
                        <span>{emoji}</span>
                        <span className="text-[#6B7280] tabular-nums">{list.length}</span>
                    </span>
                );
            })}
        </div>
    );
}

// Phase B.3.2 — Picker en React Portal + position:fixed. Évite toute
// coupure par un parent `overflow:hidden/auto`. Position calculée depuis
// le `getBoundingClientRect` du bouton Smile : flip à gauche si trop près
// du bord droit, flip en haut si trop près du bas de l'écran.
// Fermeture : outside click, Escape, ou sélection d'un emoji.
const EmojiPickerLazy = React.lazy(() => import('emoji-picker-react'));

const PICKER_W = 320;
const PICKER_H = 400;
const PICKER_MARGIN = 8;

function ReactionPicker({ onReact, message }) {
    const [open, setOpen] = React.useState(false);
    const [coords, setCoords] = React.useState(null);
    const triggerRef = React.useRef(null);
    const popupRef = React.useRef(null);

    const computeCoords = React.useCallback(() => {
        if (!triggerRef.current) return null;
        const rect = triggerRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let left = rect.left;
        let top = rect.bottom + PICKER_MARGIN;
        if (left + PICKER_W + PICKER_MARGIN > vw) {
            left = Math.max(PICKER_MARGIN, rect.right - PICKER_W);
        }
        if (top + PICKER_H + PICKER_MARGIN > vh) {
            top = Math.max(PICKER_MARGIN, rect.top - PICKER_H - PICKER_MARGIN);
        }
        return { left: Math.max(PICKER_MARGIN, left), top: Math.max(PICKER_MARGIN, top) };
    }, []);

    React.useEffect(() => {
        if (!open) return;
        setCoords(computeCoords());
        const onDocClick = (e) => {
            if (popupRef.current && popupRef.current.contains(e.target)) return;
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onScroll = () => setCoords(computeCoords());
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onScroll);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onScroll);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open, computeCoords]);

    const myReaction = React.useMemo(() => {
        // On ne distingue plus `self` ici (Whapi est la source de vérité) ;
        // le choix "même emoji → remove" est tranché côté serveur.
        return null;
    }, []);

    const choose = (emoji) => {
        setOpen(false);
        const next = myReaction && myReaction.emoji === emoji ? '' : emoji;
        onReact && onReact(message, next);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                data-testid="messagerie-msg-react-trigger"
                aria-label="Ajouter une r\u00e9action"
                onClick={() => setOpen((v) => !v)}
                className="opacity-0 group-hover/bubble:opacity-100 transition-opacity h-6 w-6 rounded-full inline-flex items-center justify-center text-[#6B7280] hover:bg-[#F5F6FA] hover:text-[#4B3FD8]"
            >
                <Smile size={16} strokeWidth={1.75} />
            </button>
            {open && coords ? createPortal(
                <div
                    ref={popupRef}
                    data-testid="messagerie-msg-react-popup"
                    style={{ position: 'fixed', left: coords.left, top: coords.top, zIndex: 9999 }}
                >
                    <React.Suspense fallback={
                        <div className="px-3 py-2 rounded-md bg-white border border-[#E5E7EB] shadow-md text-xs text-[#6B7280]">
                            Chargement\u2026
                        </div>
                    }>
                        <EmojiPickerLazy
                            onEmojiClick={(data) => choose(data.emoji)}
                            autoFocusSearch={false}
                            searchPlaceHolder="Rechercher"
                            previewConfig={{ showPreview: false }}
                            lazyLoadEmojis
                            height={PICKER_H}
                            width={PICKER_W}
                        />
                    </React.Suspense>
                </div>,
                document.body,
            ) : null}
        </>
    );
}

function MessageBubble({ message, onRetry, onReact, isGroup, showSender, repliedTo, conversation }) {
    const isOut = message.direction === 'outbound';
    const isDeleted = message.deleted === true;
    const isEdited = message.edited === true;
    const hasBody = !isDeleted && !!(message.body && message.body.length);
    const hasMedia = !isDeleted && !!(message.has_media && message.media);
    const hasR2Media = !!(hasMedia && message.media.r2_key);
    const status = message.status || (isOut ? 'sent' : null);
    const handleRetry = () => onRetry && onRetry(message);
    // Phase A.2 — Aperçu de citation. Rendu uniquement si le message
    // référence un autre message (reply_to_whapi_id non vide). Si le
    // référent n'est pas dans la liste chargée, label générique en italique.
    const hasReplyRef = !isDeleted && !!message.reply_to_whapi_id;
    // Phase 4 — dans un groupe, l'attribution est essentielle : petit avatar
    // + nom au-dessus de la bulle pour la 1re occurrence d'un expéditeur.
    const senderName = message.sender_display_name
        || (message.sender_phone && `+${message.sender_phone}`)
        || (message.from_phone && `+${message.from_phone}`)
        || (message.from && message.from.split('@')[0])
        || 'Participant';
    const senderKey = message.sender_wa_id || message.from_phone || message.from || senderName;

    return (
        <div
            data-testid="messagerie-msg"
            data-direction={message.direction}
            data-status={isOut ? status : undefined}
            data-has-media={hasMedia ? 'true' : 'false'}
            data-media-kind={hasR2Media ? (message.media.kind || '') : undefined}
            data-deleted={isDeleted ? 'true' : undefined}
            data-edited={isEdited ? 'true' : undefined}
            className={'flex gap-2 ' + (isOut ? 'justify-end' : 'justify-start')}
        >
            {/* Phase 4 — avatar expéditeur (groupes, inbound, 1re occurrence) */}
            {isGroup && !isOut && showSender ? (
                <div className="mt-4">
                    <Avatar
                        name={senderName}
                        waId={senderKey}
                        sizeClass="w-7 h-7"
                        textClass="text-[10px]"
                        testId="messagerie-msg-sender-avatar"
                    />
                </div>
            ) : (isGroup && !isOut) ? (
                <div className="w-7 shrink-0" aria-hidden="true" />
            ) : null}
            <div className="flex flex-col max-w-[70%] group/bubble">
                {isGroup && !isOut && showSender ? (
                    <span
                        data-testid="messagerie-msg-sender-name"
                        data-sender-key={senderKey}
                        className="ml-2 mb-0.5 text-[11px] font-medium"
                        style={{ color: avatarColorFor(senderKey) }}
                    >
                        {senderName}
                    </span>
                ) : null}
                <div className="relative flex items-center gap-1 self-stretch">
                    {/* Phase B.3.1 — Picker uniquement sur bulles INBOUND :
                        un agent ne réagit pas à son propre message. Les pills
                        de réactions reçues restent affichées sur les outbound.
                        Phase F2 — pas de picker sur message supprimé. */}
                    <div
                        className={
                            'px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm flex-1 ' +
                            (isOut
                                ? 'bg-[#DCF8C6] text-[#1A1D2E] rounded-br-sm'
                                : 'bg-white text-[#1A1D2E] rounded-bl-sm border border-[#E5E7EB]')
                        }
                    >
                        {isDeleted ? (
                            <span
                                data-testid="messagerie-msg-deleted"
                                className="italic text-[#6B7280]"
                            >
                                🚫 Message supprimé
                            </span>
                        ) : (
                            <>
                                {hasReplyRef ? (
                                    <QuotedPreview
                                        repliedTo={repliedTo}
                                        isOutboundOwner={isOut}
                                        conversation={conversation}
                                    />
                                ) : null}
                                {hasMedia ? (
                                    <div className={hasBody ? 'mb-1.5' : ''}>
                                        <MediaAttachment media={message.media} />
                                    </div>
                                ) : null}
                                {hasBody ? <MessageBody text={message.body || ''} /> : null}
                                {!hasMedia && !hasBody ? (
                                    <span className="italic text-[#6B7280]">[Message vide]</span>
                                ) : null}
                            </>
                        )}
                        <div
                            className={
                                'mt-1 text-[10px] tabular-nums inline-flex items-center gap-1 ' +
                                (isOut ? 'text-[#4E7A2E]' : 'text-[#6B7280]')
                            }
                        >
                            <span>{casaTime(message.timestamp)}</span>
                            {isEdited && !isDeleted ? (
                                <span
                                    data-testid="messagerie-msg-edited"
                                    className="italic opacity-80"
                                    title="Message modifié"
                                >
                                    (modifié)
                                </span>
                            ) : null}
                            {isOut && <OutboundStatusIcon status={status} onRetry={handleRetry} />}
                        </div>
                    </div>
                    {!isOut && !isDeleted ? <ReactionPicker onReact={onReact} message={message} /> : null}
                </div>
                {!isDeleted && Array.isArray(message.reactions) && message.reactions.length > 0 ? (
                    <ReactionPills
                        reactions={message.reactions}
                        align={isOut ? 'end' : 'start'}
                    />
                ) : null}
            </div>
        </div>
    );
}

// A very thin ScrollArea wrapper — we use a plain div for the left list so we
// can attach a per-list scroll handler if we ever need "load more" pagination.
// The right thread's scroll is owned by MessageStream (with pin-to-bottom
// behaviour), NOT here.
function ScrollAreaSmart({ children, className = '', ...rest }) {
    return (
        <div className={'overflow-y-auto ' + className} {...rest}>
            {children}
        </div>
    );
}

// -----------------------------------------------------------------------
// Phase 4 — Panneau latéral participants (groupes)
// -----------------------------------------------------------------------
// Read-only par design (cf. brief : pas d'actions admin de groupe depuis
// Nivo). Badge "admin" pour les participants promus, avatars/initiales
// selon `profile_picture_r2_key` (fallback deterministe).
function ParticipantsPanel({ group, loading, onClose }) {
    const participants = (group && group.participants) || [];
    const admins = participants.filter((p) => p.is_admin);
    const others = participants.filter((p) => !p.is_admin);
    return (
        <aside
            data-testid="messagerie-participants-panel"
            className="w-[320px] shrink-0 h-full border-l border-[#E5E7EB] bg-white flex flex-col"
        >
            <div className="h-14 shrink-0 px-4 border-b border-[#E5E7EB] flex items-center justify-between">
                <div className="min-w-0 flex items-center gap-2">
                    <Users size={16} strokeWidth={2} className="text-[#4B3FD8]" />
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1A1D2E] truncate">
                            Participants
                        </div>
                        <div className="text-[11px] text-[#6B7280]">
                            {loading ? 'Chargement…' : `${participants.length} participant${participants.length > 1 ? 's' : ''}`}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="messagerie-participants-close"
                    onClick={onClose}
                    className="w-7 h-7 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F5F6FA] inline-flex items-center justify-center"
                    title="Fermer"
                    aria-label="Fermer"
                >
                    <CloseIcon size={14} strokeWidth={2} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="p-4 text-center text-xs text-[#6B7280]">
                        Chargement des participants…
                    </div>
                ) : (participants.length === 0 ? (
                    <div className="p-4 text-center text-xs text-[#6B7280]">
                        Aucun participant connu pour ce groupe.
                    </div>
                ) : (
                    <>
                        {admins.length ? (
                            <ParticipantsGroup label="Administrateurs" people={admins} />
                        ) : null}
                        {others.length ? (
                            <ParticipantsGroup label="Membres" people={others} />
                        ) : null}
                    </>
                ))}
            </div>
        </aside>
    );
}

function ParticipantsGroup({ label, people }) {
    return (
        <div>
            <div className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">
                {label}
            </div>
            <ul className="divide-y divide-[#F3F4F6]">
                {people.map((p) => (
                    <li
                        key={p.wa_id || p.phone_number || Math.random()}
                        data-testid="messagerie-participant-row"
                        data-wa-id={p.wa_id || ''}
                        data-is-admin={p.is_admin ? 'true' : 'false'}
                        className="px-4 py-2 flex items-center gap-3"
                    >
                        <Avatar
                            name={p.display_name}
                            phone={p.phone_number}
                            waId={p.wa_id}
                            r2Key={p.profile_picture_r2_key}
                            sizeClass="w-8 h-8"
                            textClass="text-[11px]"
                            testId="messagerie-participant-avatar"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#1A1D2E] truncate">
                                {p.display_name || (p.phone_number ? `+${p.phone_number}` : 'Participant')}
                            </div>
                            {p.display_name && p.phone_number ? (
                                <div className="text-[11px] text-[#6B7280]">
                                    +{p.phone_number}
                                </div>
                            ) : null}
                        </div>
                        {p.is_admin ? (
                            <span
                                data-testid="messagerie-participant-admin-badge"
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-medium bg-[#4B3FD8]/10 text-[#4B3FD8] border border-[#4B3FD8]/30"
                                title="Administrateur"
                            >
                                <Shield size={10} strokeWidth={2.25} />
                                Admin
                            </span>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}
